/**
 * Merges source sets into a destination set. Mutates the destination set.
 */
export function mergeSets<T>(
  destination: Set<T>,
  ...sources: ReadonlyArray<ReadonlySet<T>>
): void {
  for (const source of sources) {
    for (const value of source) {
      destination.add(value);
    }
  }
}

/**
 * Returns true if the sets have any elements in common.
 */
export function doSetsOverlap<T>(a: Set<T>, b: Set<T>): boolean {
  return Array.from(a).some((x) => b.has(x));
}

/**
 * Takes a collection of sets and merges together any that overlap.
 */
export function mergeOverlappingSets<T>(sets: Array<Set<T>>): Array<Set<T>> {
  const result: Array<Set<T>> = [];
  const remaining = new Set(sets);

  while (remaining.size > 0) {
    // Take first set as current merged set
    const current = remaining.values().next().value;
    remaining.delete(current);

    let changed = true;
    while (changed) {
      changed = false;

      // Look for any remaining sets that overlap with current
      for (const set of remaining) {
        if (doSetsOverlap(current, set)) {
          // union can't be used here since it would say ".union is not a function"
          mergeSets(current, set);
          remaining.delete(set);
          changed = true;
        }
      }
    }

    result.push(current);
  }

  return result;
}

/**
 * Returns a Set of values from the first Set argument that are not present in
 * the other Set arguments.
 */
export function setDifference<T>(
  a: ReadonlySet<T>,
  ...others: ReadonlyArray<ReadonlySet<T>>
): Set<T> {
  const result = new Set<T>();
  for (const x of a) {
    let found = false;
    for (const b of others) {
      if (b.has(x)) {
        found = true;
        break;
      }
    }
    if (!found) {
      result.add(x);
    }
  }
  return result;
}

export function symmetricDifference<T>(a: Set<T>, b: Set<T>): Set<T> {
  const difference = new Set<T>();
  // union can't be used here since it would say ".union is not a function"
  mergeSets(difference, setDifference(a, b), setDifference(b, a));
  return difference;
}
